import { useMemo, useCallback, memo } from 'react'
import { useAppDispatch } from 'hooks'

import { generateRawAnswer } from 'Helpers/SettingsHelper'
import { Question, editAnswer, AnswerProp, deleteAnswer } from 'Slices/Settings/SettingsSlice'
import StandardCheckbox from './AnswerCellType/StandardCheckbox'
import StandardSelect from './AnswerCellType/StandardSelect'
import StandardInput from './AnswerCellType/StandardInput'
import InputNumberDiwa from 'Components/Generic/InputNumberDiwa'
import StandardTreeSelect from './AnswerCellType/StandardTreeSelect'
import { AnswerType, SettingsAnswerEmptyValue } from 'Constants/Settings'

interface DynamicAnswerByTypeProps {
  respondingEntityId: string
  question: Question
  userCanEdit: boolean
  storedValue: string | AnswerProp[]
}

function DynamicAnswerByType(props: DynamicAnswerByTypeProps) {
  const dispatch = useAppDispatch()

  const storeValue = useCallback(
    (answer: string | AnswerProp[], editInfo?: object) => {
      const emptyAnswer = []
      emptyAnswer.push(
        //create empty Answer
        generateRawAnswer(
          SettingsAnswerEmptyValue,
          props.question.code,
          props.respondingEntityId,
          props.question.settingsName,
        ),
      )

      if (props.question.isMultiAnswers) {
        const checkedAnswers = answer as AnswerProp[]
        const rawAnswers = checkedAnswers.map(answerobj => {
          return generateRawAnswer(
            answerobj.value,
            props.question.code,
            props.respondingEntityId,
            props.question.settingsName,
            answerobj.priority,
          )
        })
        if (editInfo) {
          dispatch(
            deleteAnswer({
              settingsName: props.question.settingsName,
              editInfo: editInfo,
            }),
          )
        }
        dispatch(
          editAnswer({
            settingsName: props.question.settingsName,
            questionCode: props.question.code,
            respondingEntityId: props.respondingEntityId,
            isMultiAnswers: true,
            rawAnswer: rawAnswers.length > 0 ? rawAnswers : emptyAnswer,
          }),
        )

        return
      }

      dispatch(
        editAnswer({
          settingsName: props.question.settingsName,
          questionCode: props.question.code,
          respondingEntityId: props.respondingEntityId,
          rawAnswer:
            answer.length > 0
              ? generateRawAnswer(
                  answer as string,
                  props.question.code,
                  props.respondingEntityId,
                  props.question.settingsName,
                )
              : emptyAnswer,
        }),
      )
    },
    [dispatch, props.respondingEntityId, props.question],
  )

  const dynamicComponent = useMemo(() => {
    switch (props.question.typeOfAnswer) {
      case AnswerType.conditional:
        return (
          <StandardCheckbox
            storedValue={props.storedValue}
            storeValue={storeValue}
            userCanEdit={props.userCanEdit}
            question={props.question}
            respondingEntityId={props.respondingEntityId}
          />
        )
      case AnswerType.text:
        if (
          typeof props.question.possibleValues === 'object' &&
          Object.entries(props.question.possibleValues).length > 0
        ) {
          if (props.question.isMultiAnswers) {
            return (
              <StandardTreeSelect
                possibleValues={props.question.possibleValues}
                storedValue={props.storedValue as AnswerProp[]}
                storeValue={storeValue}
                userCanEdit={props.userCanEdit}
                question={props.question}
                respondingEntityId={props.respondingEntityId}
                maxTagCount={'responsive'}
              />
            )
          }
          return (
            <StandardSelect
              possibleValues={props.question.possibleValues}
              storedValue={props.storedValue}
              storeValue={storeValue}
              userCanEdit={props.userCanEdit}
              isOptional={props.question.isOptional}
            />
          )
        }

        return <StandardInput storedValue={props.storedValue} storeValue={storeValue} userCanEdit={props.userCanEdit} />
      case AnswerType.numeric:
        return (
          <InputNumberDiwa
            className='numeric-input input-check ma-0'
            min={'0'}
            defaultValue={parseInt(props.storedValue as string)}
            onValueConfirm={storeValue}
            type='number'
            storeAsString={true}
          />
        )
      default:
        return null
    }
  }, [props.question, props.storedValue, props.userCanEdit, storeValue, props.respondingEntityId])

  return dynamicComponent
}

export default memo(DynamicAnswerByType)
